import React, { useState, useEffect } from 'react';
import { expensesAPI } from '../services/api';

const ExpenseTable = ({ expenses, onEdit, onDelete, isAdmin }) => {
  const [sortedExpenses, setSortedExpenses] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'desc'
  });

  useEffect(() => {
    sortExpenses(expenses, sortConfig.key, sortConfig.direction);
  }, [expenses, sortConfig]);

  const sortExpenses = (expensesToSort, key, direction) => {
    const sorted = [...expensesToSort].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      // Handle date sorting
      if (key === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // Handle amount sorting (convert to number)
      if (key === 'amount') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      // Handle year sorting
      if (key === 'financial_year') {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      // Handle string sorting (case-insensitive)
      if (key === 'description') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setSortedExpenses(sorted);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    
    // If clicking the same column, toggle direction
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return ' ⇅'; // Both arrows when not sorted
    }
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const handleView = async (id) => {
    try {
      const response = await expensesAPI.getFile(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Clean up the URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      alert('Failed to load PDF file');
      console.error('Error loading file:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatYearRange = (year) => {
    // Handle both string (2024-2025) and number (2024) formats
    if (typeof year === 'string' && year.includes('-')) {
      return year; // Already formatted
    }
    const yearNum = parseInt(year);
    return `${yearNum} - ${yearNum + 1}`;
  };

  if (expenses.length === 0) {
    return (
      <div className="empty-state">
        <h3>No expenses found</h3>
        <p>Start by adding your first expense</p>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th onClick={() => handleSort('date')} className="sortable">
              Date{getSortIcon('date')}
            </th>
            <th onClick={() => handleSort('financial_year')} className="sortable">
              Year{getSortIcon('financial_year')}
            </th>
            <th onClick={() => handleSort('description')} className="sortable">
              Description{getSortIcon('description')}
            </th>
            <th onClick={() => handleSort('amount')} className="sortable">
              Amount{getSortIcon('amount')}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedExpenses.map((expense) => (
            <tr key={expense.id}>
              <td>{formatDate(expense.date)}</td>
              <td><strong>{formatYearRange(expense.financial_year)}</strong></td>
              <td>{expense.description}</td>
              <td><strong>{formatAmount(expense.amount)}</strong></td>
              <td>
                <div className="actions">
                  {expense.file_name && (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleView(expense.id)}
                      title="View PDF"
                    >
                      📄 View
                    </button>
                  )}
                  {isAdmin && (
                    <>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => onEdit(expense)}
                        title="Edit"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this expense?')) {
                            onDelete(expense.id);
                          }
                        }}
                        title="Delete"
                      >
                        🗑️ Delete
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ExpenseTable;